import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import api from '../utils/api';
import {
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

// Canonical fields we import. Header matching is case-insensitive and ignores
// spaces/underscores so a wide range of spreadsheet headers "just work".
const FIELD_ALIASES: Record<string, string[]> = {
  propertyAddress: ['propertyaddress', 'address', 'property', 'streetaddress', 'street'],
  unitNumber: ['unitnumber', 'unit', 'unitno', 'apt', 'apartment', 'suite'],
  tenantName: ['tenantname', 'name', 'tenant', 'residentname', 'resident', 'fullname'],
  tenantEmail: ['tenantemail', 'email', 'emailaddress', 'residentemail'],
  tenantPhone: ['tenantphone', 'phone', 'phonenumber', 'contact', 'contactnumber', 'mobile'],
  monthlyRent: ['monthlyrent', 'rent', 'rentamount', 'amount', 'monthlyamount'],
  dueDate: ['duedate', 'due', 'rentdue', 'dueday', 'dayofmonth'],
  city: ['city', 'town'],
  state: ['state', 'province', 'region'],
  zipCode: ['zipcode', 'zip', 'postalcode', 'postcode'],
};

const TEMPLATE_HEADERS = [
  'Property Address',
  'Unit Number',
  'Tenant Name',
  'Tenant Email',
  'Tenant Phone',
  'Monthly Rent',
  'Due Date',
  'City',
  'State',
  'Zip',
];

const TEMPLATE_SAMPLE = [
  ['123 Oak Street', '1A', 'John Smith', 'john.smith@example.com', '555-0101', '1500', '1', 'Springfield', 'IL', '62704'],
  ['123 Oak Street', '1B', 'Maria Garcia', 'maria.garcia@example.com', '555-0102', '1450', '1', 'Springfield', 'IL', '62704'],
  ['45 Maple Avenue', '', 'David Lee', 'david.lee@example.com', '555-0103', '2200', '5', 'Springfield', 'IL', '62704'],
];

interface NormalizedRow {
  addressName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  tenantName: string;
  tenantEmail: string;
  tenantPhone: string;
  monthlyRent: number;
  dueDay: number;
}

interface RowResult {
  rowNumber: number;
  status: 'valid' | 'error' | 'warning';
  errors: string[];
  warnings: string[];
  normalized: NormalizedRow | null;
  willCreateProperty: boolean;
  willCreateTenant: boolean;
}

interface ValidationSummary {
  total: number;
  valid: number;
  errors: number;
  warnings: number;
  newProperties: number;
  newTenants: number;
  reusedProperties: number;
  reusedTenants: number;
  rentSchedules: number;
  planLimit: number | null;
  currentAddressCount: number;
  planLimitExceeded: boolean;
}

interface ValidationResponse {
  rows: RowResult[];
  summary: ValidationSummary;
}

interface CommitResponse {
  summary: {
    rowsSubmitted: number;
    createdProperties: number;
    createdTenants: number;
    assignmentsMade: number;
    rentSchedulesCreated: number;
    failed: number;
  };
  rowOutcomes: { rowNumber: number; status: string; message: string }[];
  credentials: { name: string; email: string; tempPassword: string }[];
}

type RawRow = Record<string, string | number>;

const normalizeHeader = (h: string) => h.toLowerCase().replace(/[\s_]/g, '');

// Map a parsed record's keys onto our canonical field names.
const mapRow = (record: Record<string, any>, rowNumber: number): RawRow => {
  const out: RawRow = { rowNumber };
  const lookup: Record<string, any> = {};
  Object.keys(record).forEach((k) => {
    lookup[normalizeHeader(k)] = record[k];
  });
  Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
    for (const alias of aliases) {
      if (lookup[alias] !== undefined && lookup[alias] !== null && String(lookup[alias]).trim() !== '') {
        out[field] = lookup[alias];
        break;
      }
    }
  });
  return out;
};

const BulkImport: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFileName('');
    setRawRows([]);
    setValidation(null);
    setCommitResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: TEMPLATE_HEADERS, data: TEMPLATE_SAMPLE });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blockboard-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleParsedRecords = async (records: Record<string, any>[]) => {
    const mapped = records
      .map((r, i) => mapRow(r, i + 1))
      // drop completely empty rows
      .filter((r) => Object.keys(r).some((k) => k !== 'rowNumber' && String(r[k]).trim() !== ''));

    if (mapped.length === 0) {
      setError('No data rows found in the file. Make sure the first row contains column headers.');
      return;
    }
    setRawRows(mapped);
    await validate(mapped);
  };

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    setError('');

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (res) => {
          handleParsedRecords(res.data as Record<string, any>[]);
        },
        error: () => setError('Could not parse the CSV file.'),
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      import('read-excel-file/browser')
        .then(({ default: readXlsxFile }) => readXlsxFile(file))
        .then((rows: any[]) => {
          if (rows.length < 2) {
            setError('No data rows found in the file. Make sure the first row contains column headers.');
            return;
          }
          const headers = (rows[0] as any[]).map((h) => String(h ?? ''));
          const records: Record<string, any>[] = rows.slice(1).map((row) => {
            const obj: Record<string, any> = {};
            headers.forEach((h, i) => { obj[h] = (row as any[])[i] ?? ''; });
            return obj;
          });
          handleParsedRecords(records);
        })
        .catch(() => setError('Could not parse the Excel file.'));
    } else {
      setError('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
    }
  };

  const validate = async (rows: RawRow[]) => {
    setLoading(true);
    setError('');
    setValidation(null);
    setCommitResult(null);
    try {
      const res = await api.post<ValidationResponse>('/api/bulk-import/validate', { rows });
      setValidation(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Validation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!validation) return;
    setImporting(true);
    setError('');
    try {
      const res = await api.post<CommitResponse>('/api/bulk-import/commit', { rows: rawRows });
      setCommitResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const downloadCredentials = () => {
    if (!commitResult?.credentials?.length) return;
    const csv = Papa.unparse({
      fields: ['Name', 'Email', 'Temporary Password'],
      data: commitResult.credentials.map((c) => [c.name, c.email, c.tempPassword]),
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blockboard-tenant-credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = validation?.summary;

  return (
    <div className="space-y-6">
      {/* Intro / actions */}
      <div className="glass-card p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <TableCellsIcon className="w-7 h-7 text-[#22d3ee]" />
              Bulk Import
            </h2>
            <p className="text-white/60 mt-1 max-w-2xl">
              Onboard your entire portfolio at once. Upload a CSV or Excel file with your
              properties and tenants — we'll create the properties, tenant accounts, assign
              tenants to units, and set up their monthly rent. Review everything before it's saved.
            </p>
          </div>
          <button onClick={downloadTemplate} className="btn-secondary whitespace-nowrap">
            <DocumentArrowDownIcon className="w-5 h-5" />
            <span className="ml-2">Download Template</span>
          </button>
        </div>

        {/* Upload zone */}
        <div className="mt-6">
          <label
            htmlFor="bulk-file"
            className="flex flex-col items-center justify-center w-full p-8 rounded-2xl border-2 border-dashed border-white/15 hover:border-[#22d3ee]/60 bg-white/5 hover:bg-white/[0.07] transition-colors cursor-pointer"
          >
            <ArrowUpTrayIcon className="w-10 h-10 text-white/50 mb-3" />
            <span className="text-white font-medium">
              {fileName ? fileName : 'Click to upload a CSV or Excel file'}
            </span>
            <span className="text-white/50 text-sm mt-1">
              Required columns: Property Address, Tenant Name, Tenant Email, Monthly Rent
            </span>
            <input
              id="bulk-file"
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={onFileSelected}
            />
          </label>
          {fileName && (
            <div className="mt-3 flex justify-end">
              <button onClick={reset} className="text-sm text-white/60 hover:text-white">
                Clear file
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="glass-card p-4 border border-red-500/40 bg-red-500/10 text-red-200 flex items-start gap-2">
          <XCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="glass-card p-6 text-center text-white/70">Validating your file…</div>
      )}

      {/* Validation preview */}
      {validation && !commitResult && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Rows" value={summary!.total} />
            <SummaryCard label="Ready to import" value={summary!.valid} tone="green" />
            <SummaryCard label="Warnings" value={summary!.warnings} tone="amber" />
            <SummaryCard label="Errors" value={summary!.errors} tone="red" />
            <SummaryCard label="New properties" value={summary!.newProperties} />
            <SummaryCard label="New tenants" value={summary!.newTenants} />
            <SummaryCard label="Reused tenants" value={summary!.reusedTenants} />
            <SummaryCard label="Rent schedules" value={summary!.rentSchedules} />
          </div>

          {summary!.planLimitExceeded && (
            <div className="glass-card p-4 border border-red-500/40 bg-red-500/10 text-red-200">
              This import would exceed your plan limit of {summary!.planLimit} properties
              (you currently have {summary!.currentAddressCount}). Rows beyond the limit are marked
              as errors and will be skipped. Upgrade your plan to import everything.
            </div>
          )}

          {/* Preview table */}
          <div className="glass-card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/50 border-b border-white/10">
                    <th className="px-4 py-3 font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Property</th>
                    <th className="px-4 py-3 font-medium">Tenant</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Rent</th>
                    <th className="px-4 py-3 font-medium">Due</th>
                    <th className="px-4 py-3 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {validation.rows.map((r) => (
                    <tr key={r.rowNumber} className="border-b border-white/5 hover:bg-white/[0.03]">
                      <td className="px-4 py-3 text-white/60">{r.rowNumber}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 text-white">
                        {r.normalized?.addressName || '—'}
                        {r.willCreateProperty ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-300/80">new</span>
                        ) : r.normalized ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-white/40">existing</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {r.normalized?.tenantName || '—'}
                        {r.normalized && !r.willCreateTenant && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-white/40">existing</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/70">{r.normalized?.tenantEmail || '—'}</td>
                      <td className="px-4 py-3 text-white/70">
                        {r.normalized ? `$${r.normalized.monthlyRent.toLocaleString()}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-white/70">{r.normalized?.dueDay ?? '—'}</td>
                      <td className="px-4 py-3 text-white/60 max-w-xs">
                        {[...r.errors, ...r.warnings].map((m, i) => (
                          <div key={i} className={r.errors.includes(m) ? 'text-red-300' : 'text-amber-300'}>
                            {m}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Commit bar */}
          <div className="glass-card p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/70 text-sm">
              {summary!.valid > 0
                ? `${summary!.valid} row${summary!.valid === 1 ? '' : 's'} will be imported. ${summary!.errors} error row${summary!.errors === 1 ? '' : 's'} will be skipped.`
                : 'No rows are ready to import. Fix the errors above and re-upload.'}
            </p>
            <div className="flex gap-3">
              <button onClick={reset} className="btn-secondary">Cancel</button>
              <button
                onClick={commit}
                disabled={importing || summary!.valid === 0}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? 'Importing…' : `Import ${summary!.valid} Row${summary!.valid === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Commit result */}
      {commitResult && (
        <>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircleIcon className="w-8 h-8 text-emerald-400" />
              <h3 className="text-xl font-bold text-white">Import complete</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <SummaryCard label="Properties created" value={commitResult.summary.createdProperties} tone="green" />
              <SummaryCard label="Tenants created" value={commitResult.summary.createdTenants} tone="green" />
              <SummaryCard label="Assignments" value={commitResult.summary.assignmentsMade} />
              <SummaryCard label="Rent schedules" value={commitResult.summary.rentSchedulesCreated} />
              <SummaryCard label="Failed" value={commitResult.summary.failed} tone={commitResult.summary.failed ? 'red' : undefined} />
            </div>
          </div>

          {commitResult.credentials.length > 0 && (
            <div className="glass-card p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-white">Tenant login credentials</h4>
                  <p className="text-white/60 text-sm">
                    Share these temporary passwords with your new tenants. They'll be required to set
                    a new password on first login. Download and store them securely — they won't be shown again.
                  </p>
                </div>
                <button onClick={downloadCredentials} className="btn-primary whitespace-nowrap">
                  <ArrowDownTrayIcon className="w-5 h-5" />
                  <span className="ml-2">Download CSV</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-white/50 border-b border-white/10">
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Temporary Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commitResult.credentials.map((c, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="px-4 py-3 text-white">{c.name}</td>
                        <td className="px-4 py-3 text-white/70">{c.email}</td>
                        <td className="px-4 py-3 font-mono text-emerald-300">{c.tempPassword}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {commitResult.summary.failed > 0 && (
            <div className="glass-card p-4 border border-red-500/40 bg-red-500/10">
              <p className="text-red-200 text-sm mb-2 flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5" /> Some rows could not be imported:
              </p>
              <ul className="text-red-200/80 text-sm list-disc ml-6">
                {commitResult.rowOutcomes
                  .filter((o) => o.status === 'failed')
                  .map((o) => (
                    <li key={o.rowNumber}>Row {o.rowNumber}: {o.message}</li>
                  ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={reset} className="btn-primary">Import another file</button>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: number; tone?: 'green' | 'amber' | 'red' }> = ({
  label,
  value,
  tone,
}) => {
  const toneClass =
    tone === 'green'
      ? 'text-emerald-400'
      : tone === 'amber'
      ? 'text-amber-400'
      : tone === 'red'
      ? 'text-red-400'
      : 'text-white';
  return (
    <div className="glass-card p-4">
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      <div className="text-white/50 text-xs mt-1">{label}</div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: 'valid' | 'error' | 'warning' }> = ({ status }) => {
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-500/15 text-red-300">
        <XCircleIcon className="w-3.5 h-3.5" /> Error
      </span>
    );
  }
  if (status === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-300">
        <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Warning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-300">
      <CheckCircleIcon className="w-3.5 h-3.5" /> Ready
    </span>
  );
};

export default BulkImport;
